import type { Identity } from './types'

/**
 * Windows sign-in from the sign-in page. A browser sends its Kerberos ticket only
 * to sites in the Intranet zone or its allow-list, so the page asks once, quietly,
 * and falls back on the usual form without saying anything.
 *
 * An explicit sign-out is remembered for the browser session (a cookie with no
 * expiry), or the quiet try would sign the person straight back in.
 */

const SIGNED_OUT = 'qms_signed_out'

export function markSignedOut(): void {
  document.cookie = `${SIGNED_OUT}=1; path=/; SameSite=Lax`
}

export function clearSignedOut(): void {
  document.cookie = `${SIGNED_OUT}=; path=/; max-age=0; SameSite=Lax`
}

export function signedOut(cookies: string = document.cookie): boolean {
  return cookies.split(';').some((c) => c.trim() === `${SIGNED_OUT}=1`)
}

/** The button's address: a page the browser navigates to, back here either way. */
export function windowsStart(start: string, remember: boolean): string {
  return `${start}?page=1${remember ? '' : '&remember=0'}`
}

/** One quiet try: the identity, or null for any refusal. Never throws, never prompts the app's 401 handling. */
export async function tryWindowsSignIn(start: string, timeoutMs = 8000): Promise<Identity | null> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), timeoutMs)
  try {
    const res = await fetch(`${start}?remember=0`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: abort.signal,
    })
    return res.ok ? ((await res.json()) as Identity) : null
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
